/**
 * In-app sales closer hook (PLE-1328).
 * Records eligibility and holdout. Does not send a message.
 * SALES_DESK_SENDS_ENABLED is false, and sales-desk-live defaults off.
 */

export const SALES_DESK_LIVE_FLAG = "sales-desk-live"
/** Hard off for this build. A PostHog toggle cannot send. */
export const SALES_DESK_SENDS_ENABLED = false
export const SCRIPT_VERSION = "v1" as const
export const HOLDOUT_PERCENT = 20
export const HOLDOUT_SALT = "sales-closer-v1"
export const ABANDON_MIN_MS = 30 * 60 * 1000
export const ABANDON_MAX_MS = 72 * 60 * 60 * 1000
export const CHECKOUT_JUST_STARTED_MS = 2 * 60 * 1000
export const PENDING_KEY = "sales_closer_checkout_pending"
export const SEEN_KEY = "sales_closer_seen"
export const ASSISTANT_LABEL = "sales_assistant"

export type Branch = "free_at_limit" | "checkout_abandon"
export type Cohort = "treatment" | "holdout"
export type ScriptVersion = "v1" | "v2"
export type PlanTier = "starter" | "standard" | "ultimate"
export type BillingInterval = "month" | "year"
export type LeakPath = "hook" | "sales_desk" | "zendesk"

const ENUMS = {
  branch: ["free_at_limit", "checkout_abandon"],
  cohort: ["treatment", "holdout"],
  script_version: ["v1", "v2"],
  disclosure_version: ["placeholder", "ple386_v1"],
  minutes_since_eligible_bucket: ["0_10", "10_60", "60_plus"],
  plan_tier: ["starter", "standard", "ultimate"],
  billing_interval: ["month", "year"],
  days_since_eligible_bucket: ["0_1", "2_3", "4_7"],
  path: ["hook", "sales_desk", "zendesk"],
} as const

type PropKey = keyof typeof ENUMS

export interface KeyValueStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface PendingCheckout {
  startedAt: number
  attemptId: string
}

/**
 * Paywall triggers that mean the free daily message cap was hit (branch A).
 * - `Daily message limit reached`: server reason from check_free_user_limits, set by useCoins.
 * - `message_limit`: chat send pre-check context and the useCoins fallback.
 * - `message_limit_error`: chat send error path (ChatError with a paywall context).
 * - `rate_limit_error`: ChatError fallback when no paywall context is passed.
 * Image, voice and creation limits are not branch A.
 */
export const FREE_AT_LIMIT_TRIGGERS: readonly string[] = [
  "Daily message limit reached",
  "message_limit",
  "message_limit_error",
  "rate_limit_error",
]

export function isFreeAtLimitTrigger(trigger: string | null): boolean {
  return trigger !== null && FREE_AT_LIMIT_TRIGGERS.includes(trigger)
}

export function isSalesDeskLive(raw: string | boolean | undefined | null): boolean {
  return raw === true || raw === "true"
}

export function mayMountAssistantThread(): boolean {
  return SALES_DESK_SENDS_ENABLED
}

export function stableBucket(input: string): number {
  let hash = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0) % 100
}

export function assignCohort(userId: string): Cohort {
  const bucket = stableBucket(`${HOLDOUT_SALT}:${userId}`)
  return bucket < HOLDOUT_PERCENT ? "holdout" : "treatment"
}

export function subjectKey(userId: string): string {
  let hash = 2166136261
  const input = `${HOLDOUT_SALT}:seen:${userId}`
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16)
}

export function sanitizeSalesCloserProperties(
  props: Record<string, unknown>
): Partial<Record<PropKey, string>> {
  const out: Partial<Record<PropKey, string>> = {}
  for (const key of Object.keys(ENUMS) as PropKey[]) {
    const value = props[key]
    const allowed = ENUMS[key] as readonly string[]
    if (typeof value === "string" && allowed.includes(value)) out[key] = value
  }
  return out
}

export function toPlanTier(tier: string): PlanTier | null {
  if (tier === "starter" || tier === "ultimate") return tier
  if (tier === "premium" || tier === "standard") return "standard"
  return null
}

export function minutesBucket(elapsedMs: number): "0_10" | "10_60" | "60_plus" {
  const minutes = elapsedMs / 60_000
  if (minutes < 10) return "0_10"
  if (minutes < 60) return "10_60"
  return "60_plus"
}

export function daysBucket(elapsedMs: number): "0_1" | "2_3" | "4_7" | null {
  if (elapsedMs < 0) return null
  const days = elapsedMs / 86_400_000
  if (days > 7) return null
  if (days <= 1) return "0_1"
  if (days <= 3) return "2_3"
  return "4_7"
}

export function utcDay(now: number): string {
  return new Date(now).toISOString().slice(0, 10)
}

function safeId(value: string): string {
  return /^[A-Za-z0-9_-]{1,80}$/.test(value) ? value : "start"
}

function readJson(storage: KeyValueStorage | null, key: string): unknown {
  if (!storage) return null
  try {
    const raw = storage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/**
 * Reads the pending checkout only when it belongs to `userId`.
 * Rows from another account, or without an owner, return null and are left in place.
 */
export function readPending(
  storage: KeyValueStorage | null,
  userId: string | null
): PendingCheckout | null {
  if (!userId) return null
  const parsed = readJson(storage, PENDING_KEY)
  if (!parsed || typeof parsed !== "object") return null
  const row = parsed as { startedAt?: unknown; attemptId?: unknown; owner?: unknown }
  if (row.owner !== subjectKey(userId)) return null
  if (typeof row.startedAt !== "number" || !Number.isFinite(row.startedAt)) return null
  if (typeof row.attemptId !== "string") return null
  return { startedAt: row.startedAt, attemptId: safeId(row.attemptId) }
}

export function browserLocalStorage(): KeyValueStorage | null {
  if (typeof window === "undefined") return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function noteCheckoutStarted(
  storage: KeyValueStorage | null,
  attemptId: string,
  now: number,
  userId: string | null
): void {
  if (!storage || !userId || !Number.isFinite(now)) return
  try {
    storage.setItem(
      PENDING_KEY,
      JSON.stringify({ startedAt: now, attemptId: safeId(attemptId), owner: subjectKey(userId) })
    )
  } catch {
    return
  }
}

/** Clears the pending checkout only when it belongs to `userId`. */
export function clearPending(storage: KeyValueStorage | null, userId: string | null): void {
  if (!storage || !readPending(storage, userId)) return
  try {
    storage.removeItem(PENDING_KEY)
  } catch {
    return
  }
}

export function checkoutJustStarted(
  pending: PendingCheckout | null,
  now: number
): boolean {
  if (!pending) return false
  const age = now - pending.startedAt
  return age >= 0 && age < CHECKOUT_JUST_STARTED_MS
}

export function hasSeen(storage: KeyValueStorage | null, key: string): boolean {
  const parsed = readJson(storage, SEEN_KEY)
  return Array.isArray(parsed) && parsed.includes(key)
}

export function markSeen(storage: KeyValueStorage | null, key: string): void {
  if (!storage) return
  const parsed = readJson(storage, SEEN_KEY)
  const prior = Array.isArray(parsed) ? parsed.filter((item) => typeof item === "string") : []
  const next = [...prior.filter((item) => item !== key), key].slice(-40)
  try {
    storage.setItem(SEEN_KEY, JSON.stringify(next))
  } catch {
    return
  }
}

export function evaluateFreeAtLimit(input: {
  signedIn: boolean
  plan: string | null
  trigger: string | null
  checkoutStarted: boolean
}): Branch | null {
  if (!input.signedIn || input.plan !== "free") return null
  if (!isFreeAtLimitTrigger(input.trigger)) return null
  if (input.checkoutStarted) return null
  return "free_at_limit"
}

/**
 * Decision when the subscribe paywall closes. The close counts only when the plan
 * is confirmed (`ready`) and the same signed-in account opened and closed it.
 */
export function evaluatePaywallClose(input: {
  ready: boolean
  signedIn: boolean
  userId: string | null
  openerUserId: string | null
  plan: string | null
  trigger: string | null
  checkoutStarted: boolean
}): Branch | null {
  if (!input.ready || !input.userId || input.openerUserId !== input.userId) return null
  return evaluateFreeAtLimit({
    signedIn: input.signedIn,
    plan: input.plan,
    trigger: input.trigger,
    checkoutStarted: input.checkoutStarted,
  })
}

export function evaluateCancelReturn(input: {
  signedIn: boolean
  plan: string | null
  canceled: boolean
  hasPlanParam: boolean
}): Branch | null {
  if (!input.signedIn || input.plan !== "free") return null
  if (!input.canceled || !input.hasPlanParam) return null
  return "checkout_abandon"
}

export function evaluateDelayedAbandon(input: {
  signedIn: boolean
  plan: string | null
  startedAt: number | null
  now: number
  paymentArrived: boolean
}): Branch | null {
  if (!input.signedIn || input.plan !== "free" || input.paymentArrived) return null
  if (input.startedAt == null || !Number.isFinite(input.startedAt)) return null
  const age = input.now - input.startedAt
  if (age < ABANDON_MIN_MS || age > ABANDON_MAX_MS) return null
  return "checkout_abandon"
}

export function buildEligibleEvent(branch: Branch, cohort: Cohort) {
  return {
    event: "sales_closer_eligible" as const,
    properties: {
      branch,
      cohort,
      script_version: SCRIPT_VERSION,
    },
  }
}

export function buildExposedEvent(branch: Branch, elapsedMs: number) {
  return {
    event: "sales_closer_exposed" as const,
    properties: sanitizeSalesCloserProperties({
      branch,
      disclosure_version: "placeholder",
      minutes_since_eligible_bucket: minutesBucket(elapsedMs),
      script_version: SCRIPT_VERSION,
    }),
  }
}

export function buildChatCheckoutStartEvent(
  branch: Branch,
  planTier: PlanTier,
  billingInterval: BillingInterval
) {
  return {
    event: "sales_closer_checkout_start" as const,
    properties: sanitizeSalesCloserProperties({
      branch,
      plan_tier: planTier,
      billing_interval: billingInterval,
      script_version: SCRIPT_VERSION,
    }),
  }
}

export function buildPaidEvent(input: {
  branch: Branch
  cohort: Cohort
  planTier: PlanTier
  billingInterval: BillingInterval
  elapsedMs: number
}) {
  const days = daysBucket(input.elapsedMs)
  if (!days) return null
  return {
    event: "sales_closer_paid" as const,
    properties: sanitizeSalesCloserProperties({
      branch: input.branch,
      cohort: input.cohort,
      plan_tier: input.planTier,
      billing_interval: input.billingInterval,
      days_since_eligible_bucket: days,
      script_version: SCRIPT_VERSION,
    }),
  }
}

export function buildHoldoutLeakEvent(branch: Branch, path: LeakPath) {
  return {
    event: "sales_closer_holdout_leak" as const,
    properties: sanitizeSalesCloserProperties({
      branch,
      path,
      script_version: SCRIPT_VERSION,
    }),
  }
}
